import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { normalizarTexto } from '../../utils/texto';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  X, Archive, ArchiveRestore, Copy, Link2, Check, Calendar, User, Flag, Tag, CheckSquare,
  CircleDot, ChevronDown, ChevronLeft, ChevronRight, Eye, Lock,
  Maximize2, Minimize2, Plus, Trash2, Search, Diamond, Globe, Hourglass,
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import Attachments from './slideover/Attachments';
import CommentsActivity from './slideover/CommentsActivity';
import RelatedEntities from './slideover/RelatedEntities';
import RecordatorioTercero from './slideover/RecordatorioTercero';
import WatchersControl from './slideover/WatchersControl';
import SilenciarControl from './slideover/SilenciarControl';
import toast from 'react-hot-toast';
import DependenciasSection from './slideover/DependenciasSection';
import ConfirmDialog from './ConfirmDialog';
import { iniciales } from './utils/helpers';
import { PriorityIcon, AsignadoAvatar, HitoChip, hitoEstado, HITO_COLOR_DEFAULT, FechaText } from './utils/displays';
import { PRIORIDADES, COLORES } from './utils/constants';

// ----- Dropdown inline reutilizable -----

const InlineSelect = ({ value, onChange, options, renderOption, renderValue, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = options.find((o) => o.key === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors text-left w-full"
      >
        {selected ? renderValue(selected) : (
          <span className="text-sm text-slate-400">{placeholder}</span>
        )}
        <ChevronDown size={12} className={`text-slate-300 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.1 }}
            className="absolute left-0 top-full mt-1 min-w-full w-max max-w-xs bg-white rounded-lg shadow-xl ring-1 ring-slate-200 py-1 z-10 max-h-72 overflow-y-auto"
          >
            {options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onMouseDown={() => { onChange(opt.key); setOpen(false); }}
                className={`w-full text-left px-2.5 py-1.5 text-sm transition-colors ${
                  value === opt.key ? 'bg-cyan-50' : 'hover:bg-slate-50'
                }`}
              >
                {renderOption ? renderOption(opt) : opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- Fila de metadata (compacta para sidebar de modal) -----

const MetaRow = ({ icon: Icon, label, children }) => (
  <div className="grid grid-cols-[100px_1fr] items-start gap-2 py-1">
    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
      {Icon && <Icon size={11} strokeWidth={2} />}
      <span>{label}</span>
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

// ----- Picker de miembro del proyecto (dropdown con buscador) -----
//
// Popover reutilizable: lista los miembros del proyecto filtrados por busqueda.
// Lo usan el chip-input de asignados (multiple) y el mini-selector de cada
// subtarea (single). `seleccionados` resalta los ya elegidos; `incluirQuitar`
// agrega una fila "Sin asignar" arriba (para el caso single).
const MiembroPicker = ({
  proyecto,
  color = '#06b6d4',
  seleccionados = [],
  onPick,
  onClear,
  incluirQuitar = false,
  align = 'left',
  width = 'w-64',
}) => {
  const [busqueda, setBusqueda] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const selSet = useMemo(() => new Set(seleccionados.map(Number)), [seleccionados]);

  const candidatos = useMemo(() => {
    const q = normalizarTexto(busqueda).trim();
    return (proyecto?.miembros || [])
      .filter((m) => !q || normalizarTexto(m.nombre).includes(q));
  }, [proyecto?.miembros, busqueda]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -4, scale: 0.98 }}
      transition={{ duration: 0.12 }}
      className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1.5 ${width} bg-white rounded-lg shadow-xl ring-1 ring-slate-200 z-50 py-1`}
    >
      <div className="px-2 pb-1 pt-0.5">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar miembro..."
            className="w-full pl-7 pr-2 py-1 text-xs bg-slate-50 border border-transparent rounded placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300 transition-colors"
          />
        </div>
      </div>

      <div className="max-h-52 overflow-y-auto">
        {incluirQuitar && (
          <button
            type="button"
            onClick={() => onClear?.()}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 transition-colors"
          >
            <span className="w-5 h-5 rounded-full border border-dashed border-slate-300 flex items-center justify-center shrink-0">
              <X size={10} className="text-slate-400" />
            </span>
            <span className="flex-1 text-slate-500">Sin asignar</span>
          </button>
        )}
        {candidatos.length === 0 ? (
          <div className="px-3 py-2 text-[11px] text-slate-400">
            {busqueda ? 'Sin coincidencias.' : 'Este proyecto no tiene miembros.'}
          </div>
        ) : (
          candidatos.map((m) => {
            const activo = selSet.has(Number(m.empleado_id));
            return (
              <button
                key={m.empleado_id}
                type="button"
                onClick={() => onPick?.(m)}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-sm transition-colors ${
                  activo ? 'bg-cyan-50' : 'hover:bg-slate-50'
                }`}
              >
                <span
                  className="w-5 h-5 rounded-full text-[10px] font-semibold flex items-center justify-center shrink-0"
                  style={activo
                    ? { backgroundColor: `${color}1f`, color }
                    : { backgroundColor: '#e2e8f0', color: '#475569' }}
                >
                  {iniciales(m.nombre)}
                </span>
                <span className="flex-1 truncate text-slate-700">{m.nombre}</span>
                {activo
                  ? <Check size={12} className="text-cyan-600" strokeWidth={2.5} />
                  : <Plus size={12} className="text-slate-300" />}
              </button>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

// ----- Chip-input de asignados (multiple) -----
//
// Muestra los asignados actuales como chips (avatar + nombre + X). Un boton "+"
// abre el MiembroPicker para agregar. Add/remove golpean los endpoints de
// asignados y refrescan via onChanged (patron optimista del modal: refetch tras
// mutar; el avatar tinta con el color del proyecto).
const AsignadosControl = ({ tareaId, asignados = [], proyecto, color = '#06b6d4', onChanged }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const asignadoIds = useMemo(() => asignados.map((a) => a.id), [asignados]);

  const agregar = async (empleadoId) => {
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/asignados`, { empleado_id: empleadoId });
      onChanged?.();
    } catch (err) {
      console.error('Error agregando asignado:', err);
    }
  };

  const quitar = async (empleadoId) => {
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/asignados/${empleadoId}`);
      onChanged?.();
    } catch (err) {
      console.error('Error quitando asignado:', err);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="flex flex-wrap items-center gap-1 -mx-1 px-1 py-0.5">
        {asignados.length === 0 ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-700 px-1 -mx-1 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
          >
            <span className="w-5 h-5 rounded-full border border-dashed border-slate-300 flex items-center justify-center">
              <Plus size={11} strokeWidth={2.5} />
            </span>
            Sin asignar
          </button>
        ) : (
          <>
            {asignados.map((a) => (
              <span
                key={a.id}
                className="group inline-flex items-center gap-1 pl-0.5 pr-1 py-0.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <AsignadoAvatar nombre={a.nombre} color={color} size={18} />
                <span className="text-xs text-slate-700 max-w-[120px] truncate">{a.nombre}</span>
                <button
                  type="button"
                  onClick={() => quitar(a.id)}
                  title="Quitar"
                  className="text-slate-300 hover:text-rose-500 transition-colors"
                >
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              title="Agregar asignado"
              className="p-1 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-400 transition-colors"
            >
              <Plus size={11} strokeWidth={2.5} />
            </button>
          </>
        )}
      </div>
      <AnimatePresence>
        {open && (
          <MiembroPicker
            proyecto={proyecto}
            color={color}
            seleccionados={asignadoIds}
            onPick={(m) => {
              if (asignadoIds.includes(m.empleado_id)) quitar(m.empleado_id);
              else agregar(m.empleado_id);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- Selector de etiquetas -----

const EtiquetasSelector = ({ tareaId, proyecto, etiquetas = [], onChanged, onProyectoChanged }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Color para la próxima etiqueta a crear. Arranca cicleado según cuántas haya
  // para no repetir siempre el mismo, pero el usuario puede cambiarlo.
  const [nuevoColor, setNuevoColor] = useState(null);
  const [creando, setCreando] = useState(false);
  // Etiquetas creadas desde esta vista: se mergean a la lista del proyecto para
  // que aparezcan al instante aunque el detalle del proyecto aún no se refresque.
  const [localCreadas, setLocalCreadas] = useState([]);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Al abrir, enfocar el buscador y resetear estado de creación.
  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const proyectoEtiquetas = useMemo(() => {
    const base = proyecto?.etiquetas || [];
    const ids = new Set(base.map((e) => e.id));
    return [...base, ...localCreadas.filter((e) => !ids.has(e.id))];
  }, [proyecto?.etiquetas, localCreadas]);

  const tareaEtIds = new Set(etiquetas.map((e) => e.id));

  const q = query.trim().toLowerCase();
  const filtradas = q
    ? proyectoEtiquetas.filter((e) => e.nombre.toLowerCase().includes(q))
    : proyectoEtiquetas;
  const existeExacta = proyectoEtiquetas.some((e) => e.nombre.trim().toLowerCase() === q);
  const puedeCrear = q.length > 0 && !existeExacta;

  const colorCrear = nuevoColor || COLORES[proyectoEtiquetas.length % COLORES.length].hex;

  const toggleEtiqueta = async (etiqueta) => {
    try {
      if (tareaEtIds.has(etiqueta.id)) {
        await axiosInstance.delete(`/api/tareas/${tareaId}/etiquetas/${etiqueta.id}`);
      } else {
        await axiosInstance.post(`/api/tareas/${tareaId}/etiquetas`, { etiqueta_id: etiqueta.id });
      }
      onChanged?.();
    } catch (err) {
      console.error('Error toggle etiqueta:', err);
    }
  };

  // Crea la etiqueta en el proyecto y la aplica de una vez a la tarea actual.
  const crearYAplicar = async () => {
    const nombre = query.trim();
    if (!nombre || creando) return;
    setCreando(true);
    try {
      const { data: nueva } = await axiosInstance.post(
        `/api/proyectos/${proyecto.id}/etiquetas`,
        { nombre, color: colorCrear }
      );
      setLocalCreadas((prev) => [...prev, nueva]);
      await axiosInstance.post(`/api/tareas/${tareaId}/etiquetas`, { etiqueta_id: nueva.id });
      setQuery('');
      setNuevoColor(null);
      onChanged?.();
      onProyectoChanged?.();
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error creando etiqueta:', err);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex flex-wrap items-center gap-1.5 px-1 -mx-1 py-1 rounded-md hover:bg-slate-50 cursor-pointer min-h-[28px]" onClick={() => setOpen(true)}>
        {etiquetas.length === 0 ? (
          <span className="text-sm text-slate-400 inline-flex items-center gap-1">
            <Plus size={11} />
            Añadir etiqueta
          </span>
        ) : (
          etiquetas.map((e) => (
            <span
              key={e.id}
              className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded text-[11px] font-medium"
              style={{
                backgroundColor: `${e.color || '#94a3b8'}1f`,
                color: e.color || '#475569',
              }}
            >
              {e.es_global ? (
                <Globe size={10} strokeWidth={2.4} className="opacity-80" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
              )}
              {e.nombre}
            </span>
          ))
        )}
        {etiquetas.length > 0 && (
          <Plus size={11} className="text-slate-300 ml-1" />
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.1 }}
            className="absolute left-0 top-full mt-1 w-64 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 z-10 overflow-hidden"
          >
            {/* Buscador / crear */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-slate-100">
              <Search size={12} className="text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && puedeCrear) { e.preventDefault(); crearYAplicar(); }
                  if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
                }}
                placeholder="Buscar o crear..."
                maxLength={120}
                className="flex-1 text-sm bg-transparent border-0 focus:outline-none placeholder-slate-400 text-slate-800 min-w-0"
              />
            </div>

            <div className="max-h-60 overflow-y-auto py-1">
              {filtradas.length === 0 && !puedeCrear ? (
                <div className="px-3 py-3 text-xs text-slate-400 text-center">
                  {proyectoEtiquetas.length === 0
                    ? 'Este proyecto aún no tiene etiquetas.'
                    : 'Sin coincidencias.'}
                </div>
              ) : (
                filtradas.map((e) => {
                  const checked = tareaEtIds.has(e.id);
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onMouseDown={() => toggleEtiqueta(e)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                        checked ? 'bg-cyan-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      {e.es_global ? (
                        <Globe size={12} strokeWidth={2.2} className="flex-shrink-0 text-slate-400" />
                      ) : (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.color || '#94a3b8' }} />
                      )}
                      <span className="flex-1 text-slate-800">{e.nombre}</span>
                      {checked && <span className="text-[10px] text-cyan-600 font-medium">Aplicada</span>}
                    </button>
                  );
                })
              )}
            </div>

            {puedeCrear && (
              <div className="border-t border-slate-100 px-2.5 py-2">
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); crearYAplicar(); }}
                  disabled={creando}
                  className="w-full flex items-center gap-2 px-1 py-1 text-left text-sm text-slate-700 hover:text-slate-900 disabled:opacity-50"
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorCrear }} />
                  <span className="flex-1 truncate">
                    Crear <span className="font-medium">«{query.trim()}»</span>
                  </span>
                  <Plus size={13} strokeWidth={2.5} className="text-slate-400 flex-shrink-0" />
                </button>
                <div className="flex items-center gap-1 mt-1.5 px-1">
                  {COLORES.map((c) => (
                    <button
                      key={c.hex}
                      type="button"
                      title={c.nombre}
                      onMouseDown={(e) => { e.preventDefault(); setNuevoColor(c.hex); }}
                      className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${
                        colorCrear === c.hex ? 'ring-2 ring-offset-1 ring-slate-900' : ''
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- Mini-selector de asignado de una subtarea -----
//
// Trigger compacto: avatar si hay asignado, circulo punteado con icono User si
// no. Abre el MiembroPicker (single: incluye "Sin asignar"). Setea via PATCH
// {asignado_id}; null para quitar.
const SubtareaAsignado = ({ tareaId, sub, proyecto, color, onChanged }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const setAsignado = async (empleadoId) => {
    setOpen(false);
    try {
      await axiosInstance.patch(`/api/tareas/${tareaId}/subtareas/${sub.id}`, { asignado_id: empleadoId });
      onChanged?.();
    } catch (err) {
      console.error('Error asignando subtarea:', err);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={sub.asignado ? `Asignado a ${sub.asignado.nombre}` : 'Asignar'}
        className="flex items-center justify-center rounded-full hover:opacity-80 transition-opacity"
      >
        {sub.asignado ? (
          <AsignadoAvatar nombre={sub.asignado.nombre} color={color} size={18} />
        ) : (
          <span className="w-[18px] h-[18px] rounded-full border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-600 transition-colors">
            <User size={10} strokeWidth={2} />
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <MiembroPicker
            proyecto={proyecto}
            color={color}
            seleccionados={sub.asignado ? [sub.asignado.id] : []}
            onPick={(m) => setAsignado(m.empleado_id)}
            onClear={() => setAsignado(null)}
            incluirQuitar={!!sub.asignado}
            align="right"
            width="w-56"
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- Mini date-picker de una subtarea -----
//
// Muestra FechaText (compact) si hay fecha, o un icono de calendario si no. El
// input date nativo va superpuesto e invisible para abrir el datepicker del SO
// al click sin romper la densidad visual. Setea via PATCH {fecha_vencimiento}.
const SubtareaFecha = ({ tareaId, sub, onChanged }) => {
  const setFecha = async (valor) => {
    try {
      await axiosInstance.patch(`/api/tareas/${tareaId}/subtareas/${sub.id}`, {
        fecha_vencimiento: valor || null,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error fechando subtarea:', err);
    }
  };

  return (
    <div className="relative flex items-center">
      {sub.fecha_vencimiento ? (
        <span className="px-1 rounded hover:bg-slate-100 transition-colors leading-none py-0.5">
          <FechaText fecha={sub.fecha_vencimiento} compact />
        </span>
      ) : (
        <span
          title="Agregar fecha"
          className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <Calendar size={11} strokeWidth={2} />
        </span>
      )}
      <input
        type="date"
        value={sub.fecha_vencimiento || ''}
        onChange={(e) => setFecha(e.target.value)}
        title={sub.fecha_vencimiento ? 'Cambiar fecha' : 'Agregar fecha'}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  );
};

// ----- Checklist (subtareas) -----

const Checklist = ({ tareaId, subtareas = [], color, proyecto, onChanged }) => {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // id
  const [editDraft, setEditDraft] = useState('');
  const [subtareaToDelete, setSubtareaToDelete] = useState(null);
  const inputRef = useRef(null);
  const editRef = useRef(null);

  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);
  useEffect(() => { if (editing) editRef.current?.focus(); }, [editing]);

  const total = subtareas.length;
  const hechas = subtareas.filter((s) => s.completado).length;
  const pct = total > 0 ? Math.round((hechas / total) * 100) : 0;

  const handleToggle = async (s) => {
    try {
      await axiosInstance.patch(`/api/tareas/${tareaId}/subtareas/${s.id}`, {
        completado: !s.completado,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error toggle subtarea:', err);
    }
  };

  const handleAdd = async () => {
    const t = draft.trim();
    if (!t) {
      setAdding(false);
      return;
    }
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/subtareas`, { texto: t });
      setDraft('');
      onChanged?.();
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error agregando subtarea:', err);
    }
  };

  const doDelete = async () => {
    if (!subtareaToDelete) return;
    const s = subtareaToDelete;
    setSubtareaToDelete(null);
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/subtareas/${s.id}`);
      onChanged?.();
    } catch (err) {
      console.error('Error eliminando subtarea:', err);
    }
  };

  const handleSaveEdit = async (s) => {
    const t = editDraft.trim();
    if (!t || t === s.texto) {
      setEditing(null);
      return;
    }
    try {
      await axiosInstance.patch(`/api/tareas/${tareaId}/subtareas/${s.id}`, { texto: t });
      setEditing(null);
      onChanged?.();
    } catch (err) {
      console.error('Error editando subtarea:', err);
    }
  };

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <CheckSquare size={11} strokeWidth={2} className="text-slate-400" />
        <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
          Checklist
        </span>
        {total > 0 && (
          <span className="text-[10px] text-slate-400 tabular-nums">
            {hechas}/{total}
          </span>
        )}
        <div className="flex-1 h-px bg-slate-200/60" />
      </div>

      {total > 0 && (
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
      )}

      <ul className="space-y-0.5 mb-1">
        <AnimatePresence initial={false}>
          {subtareas.map((s) => (
            <motion.li
              key={s.id}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="group flex items-start gap-2 px-1 -mx-1 py-1 rounded-md hover:bg-slate-50 transition-colors"
            >
              <button
                onClick={() => handleToggle(s)}
                className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
                  s.completado
                    ? 'bg-cyan-500 border-cyan-500'
                    : 'border-slate-300 hover:border-cyan-400 bg-white'
                }`}
                style={s.completado ? { backgroundColor: color, borderColor: color } : {}}
              >
                {s.completado && (
                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              {editing === s.id ? (
                <input
                  ref={editRef}
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onBlur={() => handleSaveEdit(s)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleSaveEdit(s); }
                    if (e.key === 'Escape') { setEditing(null); }
                  }}
                  className="flex-1 text-sm bg-transparent border-0 focus:outline-none px-0 py-0 text-slate-800 leading-snug"
                />
              ) : (
                <span
                  onClick={() => { setEditing(s.id); setEditDraft(s.texto); }}
                  className={`flex-1 text-sm leading-snug cursor-text break-words ${
                    s.completado ? 'line-through text-slate-400' : 'text-slate-700'
                  }`}
                >
                  {s.texto}
                </span>
              )}
              {/* Controles inline: fecha + asignado siempre visibles (cargan
                  informacion); el eliminar aparece solo en hover. */}
              <div className="flex items-center gap-1.5 flex-shrink-0 mt-px">
                <SubtareaFecha tareaId={tareaId} sub={s} onChanged={onChanged} />
                <SubtareaAsignado
                  tareaId={tareaId}
                  sub={s}
                  proyecto={proyecto}
                  color={color}
                  onChanged={onChanged}
                />
                <button
                  onClick={() => setSubtareaToDelete(s)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-rose-600 transition-opacity"
                  title="Eliminar"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      {adding ? (
        <div className="flex items-start gap-2 px-2 py-1.5 -mx-1 bg-white border border-cyan-400/50 rounded-md focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-500/15">
          <span className="mt-0.5 w-4 h-4 rounded border border-dashed border-slate-300 flex-shrink-0" />
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleAdd}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
              if (e.key === 'Escape') { setAdding(false); setDraft(''); }
            }}
            placeholder="Nuevo item..."
            className="flex-1 text-sm bg-transparent border-0 focus:outline-none placeholder-slate-400 text-slate-800"
          />
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full flex items-center gap-1.5 px-1 -mx-1 py-1 text-xs text-slate-400 hover:text-slate-700 rounded-md transition-colors"
        >
          <Plus size={11} strokeWidth={2.5} />
          Añadir item
        </button>
      )}

      <ConfirmDialog
        isOpen={!!subtareaToDelete}
        title="¿Eliminar este item?"
        message={subtareaToDelete ? `Se quitará "${subtareaToDelete.texto}" del checklist.` : ''}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={doDelete}
        onClose={() => setSubtareaToDelete(null)}
      />
    </section>
  );
};

// ----- Modal principal -----
//
// Nota arquitectural: este componente sigue exportándose como `TaskSlideOver` por compatibilidad
// con los consumidores. Conceptualmente es un modal centrado (no un drawer/slide-over),
// decisión tomada en pulido visual de 1.5.

const MAXIMIZED_STORAGE_KEY = 'proyecto-tarea-modal-maximizado';

const TaskSlideOver = ({ tareaId, initialData = null, proyecto, onClose, onUpdate, onArchive, onDuplicate, onNavigate, onProyectoChanged }) => {
  const { user } = useAuth();
  // Si llega initialData (la card que abrio el modal ya tenia datos), hidratamos
  // al instante para evitar el flash de skeleton. La carga al backend corre en
  // background y reemplaza con la version completa cuando llega.
  const [tarea, setTarea] = useState(() => (initialData && initialData.id === tareaId ? initialData : null));
  const [loading, setLoading] = useState(false);

  const [tituloDraft, setTituloDraft] = useState(initialData?.titulo || '');
  const [editingTitulo, setEditingTitulo] = useState(false);
  const [descripcionDraft, setDescripcionDraft] = useState(initialData?.descripcion || '');
  const [editingDescripcion, setEditingDescripcion] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [maximized, setMaximized] = useState(() => {
    try { return localStorage.getItem(MAXIMIZED_STORAGE_KEY) === '1'; } catch (e) { return false; }
  });
  const commentFocusRef = useRef(null);
  const descripcionRef = useRef(null);

  const toggleMaximized = useCallback(() => {
    setMaximized((m) => {
      const next = !m;
      try { localStorage.setItem(MAXIMIZED_STORAGE_KEY, next ? '1' : '0'); } catch (e) {}
      return next;
    });
  }, []);

  // Carga interna pura: solo actualiza state local. Sin deps externas, evita re-ejecutar
  // el useEffect de abajo cuando el parent re-renderiza con un onUpdate distinto.
  const cargarTarea = useCallback(async (id) => {
    try {
      const { data } = await axiosInstance.get(`/api/tareas/${id}`);
      setTarea(data);
      setTituloDraft(data.titulo);
      setDescripcionDraft(data.descripcion || '');
      return data;
    } catch (err) {
      console.error('Error cargando tarea:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!tareaId) {
      setTarea(null);
      return;
    }
    // Si ya tenemos datos en state (de initialData o del id previo), no mostramos
    // skeleton — solo refrescamos en background. Asi la apertura se siente instantánea.
    const yaHidratada = tarea && tarea.id === tareaId;
    if (!yaHidratada) setLoading(true);
    cargarTarea(tareaId).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tareaId, cargarTarea]);

  useEffect(() => { if (editingDescripcion) descripcionRef.current?.focus(); }, [editingDescripcion]);

  // Refetch tras mutaciones (etiquetas, subtareas, comentarios, adjuntos): carga la
  // tarea y propaga la versión completa al parent para que las cards del board reflejen
  // cambios al instante.
  const refetch = useCallback(async () => {
    if (!tarea?.id) return;
    const data = await cargarTarea(tarea.id);
    if (data) onUpdate?.(data);
  }, [tarea?.id, cargarTarea, onUpdate]);

  // Atajos: Esc cierra, C enfoca composer, ←/→ navegan
  useEffect(() => {
    if (!tareaId) return;
    const onKey = (e) => {
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea' || target?.isContentEditable;

      if (e.key === 'Escape' && !editingTitulo && !editingDescripcion) {
        onClose?.();
        return;
      }
      if (isEditing) return;

      if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        commentFocusRef.current?.current?.focus();
      } else if (e.key.toLowerCase() === 'm') {
        e.preventDefault();
        toggleMaximized();
      } else if (e.key === 'ArrowLeft' && onNavigate) {
        e.preventDefault();
        onNavigate('prev');
      } else if (e.key === 'ArrowRight' && onNavigate) {
        e.preventDefault();
        onNavigate('next');
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [tareaId, onClose, editingTitulo, editingDescripcion, onNavigate, toggleMaximized]);

  const patchTarea = useCallback(async (patch) => {
    if (!tarea?.id) return;
    try {
      const { data } = await axiosInstance.put(`/api/tareas/${tarea.id}`, patch);
      setTarea(data);
      // Propagar la tarea completa (no solo el patch) — así el parent siempre tiene
      // la versión más actual incluyendo campos derivados (etiquetas, contadores, etc).
      onUpdate?.(data);
    } catch (err) {
      console.error('Error actualizando tarea:', err);
      // Permisos (403) o tarea en espera (409): decir por que no se aplico.
      if (err?.response?.data?.error) toast.error(err.response.data.error);
    }
  }, [tarea?.id, onUpdate]);

  const handleSaveTitulo = () => {
    const t = tituloDraft.trim();
    if (t && t !== tarea?.titulo) {
      patchTarea({ titulo: t });
    } else {
      setTituloDraft(tarea?.titulo || '');
    }
    setEditingTitulo(false);
  };

  const handleSaveDescripcion = () => {
    if (descripcionDraft !== (tarea?.descripcion || '')) {
      patchTarea({ descripcion: descripcionDraft || null });
    }
    setEditingDescripcion(false);
  };

  const doArchivar = async () => {
    if (!tarea) return;
    try {
      await axiosInstance.post(`/api/tareas/${tarea.id}/archivar`);
      onArchive?.();
    } catch (err) {
      console.error('Error archivando:', err);
      if (err?.response?.data?.error) toast.error(err.response.data.error);
    }
  };

  const handleDesarchivar = async () => {
    if (!tarea) return;
    try {
      await axiosInstance.post(`/api/tareas/${tarea.id}/desarchivar`);
      onArchive?.();
    } catch (err) {
      console.error('Error desarchivando:', err);
      if (err?.response?.data?.error) toast.error(err.response.data.error);
    }
  };

  const handleDuplicar = async () => {
    if (!tarea) return;
    try {
      const { data } = await axiosInstance.post(`/api/tareas/${tarea.id}/duplicar`);
      onDuplicate?.(data);
    } catch (err) {
      console.error('Error duplicando:', err);
    }
  };

  const handleCopyLink = () => {
    if (!proyecto || !tarea) return;
    const url = `${window.location.origin}/proyectos/${proyecto.slug}/tareas/${tarea.numero}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 1500);
    }).catch(() => {});
  };

  // Opciones del selector de hito: "Sin hito" + los hitos del proyecto (ordenados
  // por fecha, los sin fecha al final). proyecto.hitos viene en el detalle del proyecto.
  const hitosOptions = useMemo(() => {
    const hitos = [...(proyecto?.hitos || [])].sort((a, b) => {
      if (!a.fecha) return 1;
      if (!b.fecha) return -1;
      return a.fecha.localeCompare(b.fecha);
    });
    return [
      { key: 'null', label: 'Sin hito' },
      ...hitos.map((h) => ({
        key: String(h.id),
        label: h.nombre,
        fecha: h.fecha,
        color: h.color,
        completado: h.completado,
      })),
    ];
  }, [proyecto?.hitos]);

  if (!tareaId) return null;

  const color = proyecto?.color || '#06b6d4';

  const content = (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        className={`fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm ${
          maximized ? 'p-0' : 'p-4'
        }`}
      >
        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          onClick={(e) => e.stopPropagation()}
          className={`bg-white shadow-2xl flex flex-col overflow-hidden transition-[width,height,max-width,max-height,border-radius] duration-200 ease-out ${
            maximized
              ? 'w-screen h-screen max-w-none max-h-none rounded-none'
              : 'w-full h-full max-w-6xl max-h-[92vh] rounded-2xl'
          }`}
        >
          {/* Header (toolbar full-width) */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/80 flex-shrink-0 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase flex-shrink-0">
                {tarea?.codigo || (tarea ? `${proyecto?.slug}-${tarea.numero}` : '...')}
              </span>
              {proyecto?.nombre && (
                <>
                  <span className="text-slate-200 flex-shrink-0">·</span>
                  <span className="text-xs text-slate-500 truncate">
                    {proyecto.nombre}
                  </span>
                </>
              )}
              {tarea?.archivado && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 flex-shrink-0">
                  <Archive size={9} strokeWidth={2.5} />
                  Archivada
                </span>
              )}
              {!tarea?.archivado && (tarea?.esta_bloqueada || (tarea?.bloqueadoras_pendientes?.length > 0)) && (
                <span
                  title={`Bloqueada por: ${(tarea.bloqueadoras_pendientes || []).map((b) => `${b.codigo} · ${b.titulo}`).join(' · ')}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-50 text-rose-700 flex-shrink-0"
                >
                  <Lock size={9} strokeWidth={2.5} />
                  Bloqueada
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {onNavigate && (
                <>
                  <button
                    onClick={() => onNavigate('prev')}
                    title="Anterior (←)"
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => onNavigate('next')}
                    title="Siguiente (→)"
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" />
                </>
              )}
              <button
                onClick={handleCopyLink}
                title={linkCopiado ? 'Enlace copiado' : 'Copiar enlace'}
                className={`p-1.5 rounded transition-colors ${
                  linkCopiado
                    ? 'text-emerald-600 bg-emerald-50'
                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                }`}
              >
                {linkCopiado ? <Check size={14} strokeWidth={2.5} /> : <Link2 size={14} />}
              </button>
              {tarea && (
                <WatchersControl
                  tareaId={tarea.id}
                  watchers={tarea.watchers || []}
                  proyecto={proyecto}
                  currentEmpleadoId={user?.empleado_id}
                  onChanged={refetch}
                  variant="header"
                  color={color}
                />
              )}
              {tarea && (
                <SilenciarControl
                  tareaId={tarea.id}
                  proyectoId={tarea.proyecto_id}
                  silenciada={!!tarea.silenciada}
                  proyectoSilenciado={!!tarea.proyecto_silenciado}
                  onChanged={refetch}
                />
              )}
              <button
                onClick={handleDuplicar}
                title="Duplicar tarea"
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              >
                <Copy size={14} />
              </button>
              {tarea?.archivado ? (
                <button
                  onClick={handleDesarchivar}
                  title="Desarchivar tarea"
                  className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                >
                  <ArchiveRestore size={14} />
                </button>
              ) : (
                <button
                  onClick={doArchivar}
                  title="Archivar tarea (recuperable luego)"
                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                >
                  <Archive size={14} />
                </button>
              )}
              <div className="w-px h-5 bg-slate-200 mx-1" />
              <button
                onClick={toggleMaximized}
                title={maximized ? 'Restaurar tamaño (M)' : 'Maximizar (M)'}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
              >
                {maximized
                  ? <Minimize2 size={13} strokeWidth={2} />
                  : <Maximize2 size={13} strokeWidth={2} />}
              </button>
              <button
                onClick={onClose}
                title="Cerrar (Esc)"
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Body: 2 columnas. En lg+ es grid; en mobile es flujo vertical (sidebar abajo).
              El footer con atajos vive después del body (ver ShortcutsFooter abajo). */}
          {loading || !tarea ? (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] overflow-hidden min-h-0">
              <div className="overflow-hidden px-6 py-5 skeleton">
                <div className="h-6 w-2/3 bg-slate-100 rounded mb-3" />
                <div className="h-4 w-full bg-slate-100/70 rounded mb-1.5" />
                <div className="h-4 w-5/6 bg-slate-100/70 rounded mb-1.5" />
                <div className="h-4 w-3/4 bg-slate-100/70 rounded mb-8" />
                <div className="h-3 w-20 bg-slate-100 rounded mb-3" />
                <div className="h-1 w-full bg-slate-100 rounded mb-4" />
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 rounded border border-slate-200" />
                    <div className="h-3 flex-1 max-w-[260px] bg-slate-100/70 rounded" />
                  </div>
                ))}
              </div>
              <div className="lg:border-l border-t lg:border-t-0 border-slate-200/70 bg-slate-50/40 overflow-hidden px-5 py-5 skeleton">
                <div className="h-3 w-20 bg-slate-200/60 rounded mb-4" />
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="grid grid-cols-[100px_1fr] items-center gap-2 py-1.5">
                    <div className="h-3 w-16 bg-slate-100 rounded" />
                    <div className="h-4 w-full max-w-[160px] bg-slate-100 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] overflow-hidden min-h-0">
              {/* Columna principal: título, descripción, checklist, adjuntos, comentarios */}
              <div className="overflow-y-auto px-6 py-5">
                {/* Título: display/edit */}
                {editingTitulo ? (
                  <textarea
                    value={tituloDraft}
                    onChange={(e) => setTituloDraft(e.target.value)}
                    onBlur={handleSaveTitulo}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveTitulo(); }
                      if (e.key === 'Escape') { e.preventDefault(); setTituloDraft(tarea.titulo); setEditingTitulo(false); }
                    }}
                    rows={2}
                    autoFocus
                    className="w-full text-xl font-semibold tracking-tight text-slate-900 bg-transparent border-0 focus:outline-none resize-none p-0 leading-snug"
                  />
                ) : (
                  <h2
                    onDoubleClick={() => setEditingTitulo(true)}
                    title="Doble click para editar"
                    className="text-xl font-semibold tracking-tight text-slate-900 rounded -mx-1 px-1 py-0.5 hover:bg-slate-50 leading-snug select-text"
                  >
                    {tarea.titulo}
                  </h2>
                )}

                {/* En espera: el plazo no corre hasta que se cierre la predecesora */}
                {tarea.congelada && !['hecho', 'cancelado'].includes(tarea.estado?.categoria) && (
                  <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-indigo-50/70 border border-indigo-100 text-[13px] text-indigo-900">
                    <Hourglass size={14} strokeWidth={2} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                    <span>
                      En espera de{' '}
                      <span className="font-medium">
                        {(tarea.bloqueadoras_pendientes || []).map((b) => b.codigo).join(', ')}
                      </span>
                      . No vence ni genera recordatorios; su plazo empieza a correr el día que se cierre.
                    </span>
                  </div>
                )}

                {/* Descripción: display/edit (consistente con título) */}
                {editingDescripcion ? (
                  <textarea
                    ref={descripcionRef}
                    value={descripcionDraft}
                    onChange={(e) => setDescripcionDraft(e.target.value)}
                    onBlur={handleSaveDescripcion}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setDescripcionDraft(tarea.descripcion || '');
                        setEditingDescripcion(false);
                      }
                    }}
                    placeholder="Añadir descripción..."
                    rows={4}
                    className="w-full mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/15 focus:border-cyan-500/50 resize-none placeholder-slate-400 leading-relaxed px-3 py-2 rounded-md transition-colors"
                  />
                ) : (
                  <p
                    onDoubleClick={() => setEditingDescripcion(true)}
                    onClick={() => {
                      // Cuando esta vacia, click simple basta (no hay texto para
                      // seleccionar y la unica interaccion sensata es editar).
                      if (!tarea.descripcion) setEditingDescripcion(true);
                    }}
                    title={tarea.descripcion ? 'Doble click para editar' : 'Click para añadir descripción'}
                    className={`mt-3 text-sm leading-relaxed rounded -mx-1 px-1 py-1 hover:bg-slate-50 whitespace-pre-wrap min-h-[28px] ${
                      tarea.descripcion ? 'text-slate-700 select-text' : 'text-slate-400 italic cursor-pointer'
                    }`}
                  >
                    {tarea.descripcion || 'Añadir descripción...'}
                  </p>
                )}

                {/* Checklist */}
                <div className="mt-8">
                  <Checklist
                    tareaId={tarea.id}
                    subtareas={tarea.subtareas || []}
                    color={color}
                    proyecto={proyecto}
                    onChanged={refetch}
                  />
                </div>

                {/* Comentarios + Actividad */}
                <div className="mt-8">
                  <CommentsActivity
                    tareaId={tarea.id}
                    comentarios={tarea.comentarios || []}
                    actividad={tarea.actividad || []}
                    color={color}
                    onChanged={refetch}
                    registerFocusRef={(ref) => { commentFocusRef.current = ref; }}
                  />
                </div>
              </div>

              {/* Sidebar: metadata + relaciones */}
              <div className="lg:border-l border-t lg:border-t-0 border-slate-200/70 bg-slate-50/40 overflow-y-auto px-5 py-5">
                <div className="mb-6">
                  <h3 className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase mb-2 px-1">
                    Propiedades
                  </h3>
                  <div className="-mx-1">
                    <MetaRow icon={CircleDot} label="Estado">
                      <InlineSelect
                        value={tarea.estado_id ? String(tarea.estado_id) : null}
                        onChange={(v) => patchTarea({ estado_id: parseInt(v, 10) })}
                        options={(proyecto?.estados || []).map((e) => ({
                          key: String(e.id),
                          label: e.nombre,
                          color: e.color,
                        }))}
                        renderValue={(opt) => (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: opt.color || '#94a3b8' }}
                            />
                            <span className="text-sm text-slate-800">{opt.label}</span>
                          </span>
                        )}
                        renderOption={(opt) => (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: opt.color || '#94a3b8' }}
                            />
                            {opt.label}
                          </span>
                        )}
                      />
                    </MetaRow>

                    <MetaRow icon={User} label="Asignados">
                      <AsignadosControl
                        tareaId={tarea.id}
                        asignados={tarea.asignados || []}
                        proyecto={proyecto}
                        color={color}
                        onChanged={refetch}
                      />
                    </MetaRow>

                    <MetaRow icon={Flag} label="Prioridad">
                      <InlineSelect
                        value={tarea.prioridad || 'ninguna'}
                        onChange={(v) => patchTarea({ prioridad: v })}
                        options={PRIORIDADES.map((p) => ({ key: p.key, label: p.label }))}
                        renderValue={(opt) => (
                          <span className="inline-flex items-center gap-2">
                            <span className="w-3 h-3 inline-flex items-center justify-center">
                              {opt.key === 'ninguna'
                                ? <span className="w-2 h-2 rounded-full border border-dashed border-slate-300" />
                                : <PriorityIcon prioridad={opt.key} />}
                            </span>
                            <span className="text-sm text-slate-800">{opt.label}</span>
                          </span>
                        )}
                        renderOption={(opt) => (
                          <span className="inline-flex items-center gap-2">
                            <span className="w-3 h-3 inline-flex items-center justify-center">
                              {opt.key === 'ninguna'
                                ? <span className="w-2 h-2 rounded-full border border-dashed border-slate-300" />
                                : <PriorityIcon prioridad={opt.key} />}
                            </span>
                            {opt.label}
                          </span>
                        )}
                      />
                    </MetaRow>

                    <MetaRow icon={Calendar} label="Inicio">
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={tarea.fecha_inicio || ''}
                          onChange={(e) => patchTarea({ fecha_inicio: e.target.value || null })}
                          className="text-sm text-slate-800 bg-transparent border-0 px-2 py-1 -mx-2 rounded hover:bg-slate-100 focus:outline-none focus:bg-slate-100 transition-colors tabular-nums"
                        />
                        {tarea.fecha_inicio && (
                          <button
                            onClick={() => patchTarea({ fecha_inicio: null })}
                            className="text-slate-300 hover:text-rose-500 transition-colors"
                            title="Quitar fecha"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </MetaRow>

                    <MetaRow icon={Calendar} label="Vencimiento">
                      {(() => {
                        const cat = tarea.estado?.categoria;
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const d = tarea.fecha_vencimiento ? new Date(tarea.fecha_vencimiento + 'T00:00:00') : null;
                        const vencida = d && d < today && !['hecho', 'cancelado'].includes(cat);
                        return (
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={tarea.fecha_vencimiento || ''}
                              onChange={(e) => patchTarea({ fecha_vencimiento: e.target.value || null })}
                              className={`text-sm bg-transparent border-0 px-2 py-1 -mx-2 rounded hover:bg-slate-100 focus:outline-none focus:bg-slate-100 transition-colors tabular-nums ${
                                vencida ? 'text-rose-600 font-medium' : 'text-slate-800'
                              }`}
                            />
                            {vencida && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold text-rose-700 bg-rose-50"
                                title="Esta tarea está vencida"
                              >
                                Vencida
                              </span>
                            )}
                            {tarea.fecha_vencimiento && (
                              <button
                                onClick={() => patchTarea({ fecha_vencimiento: null })}
                                className="text-slate-300 hover:text-rose-500 transition-colors ml-auto"
                                title="Quitar fecha"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </MetaRow>

                    <MetaRow icon={Diamond} label="Hito">
                      <InlineSelect
                        value={tarea.hito_id ? String(tarea.hito_id) : 'null'}
                        onChange={(v) => patchTarea({ hito_id: v === 'null' ? null : parseInt(v, 10) })}
                        options={hitosOptions}
                        placeholder="Sin hito"
                        renderValue={(opt) => {
                          if (opt.key === 'null') return <span className="text-sm text-slate-400">Sin hito</span>;
                          return <HitoChip hito={tarea.hito} />;
                        }}
                        renderOption={(opt) => {
                          if (opt.key === 'null') return <span className="text-slate-400">Sin hito</span>;
                          const { vencido } = hitoEstado(opt);
                          const c = vencido ? '#e11d48' : (opt.color || HITO_COLOR_DEFAULT);
                          return (
                            <span className="inline-flex items-center gap-2 w-full">
                              <Diamond
                                size={9}
                                strokeWidth={2.5}
                                style={{ color: c, fill: opt.completado ? c : 'none' }}
                              />
                              <span className="flex-1 truncate text-slate-800">{opt.label}</span>
                              {opt.fecha && (
                                <span className="ml-auto"><FechaText fecha={opt.fecha} compact /></span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </MetaRow>

                    <MetaRow icon={Tag} label="Etiquetas">
                      <EtiquetasSelector
                        tareaId={tarea.id}
                        proyecto={proyecto}
                        etiquetas={tarea.etiquetas || []}
                        onChanged={refetch}
                        onProyectoChanged={onProyectoChanged}
                      />
                    </MetaRow>

                    <MetaRow icon={Eye} label="Siguiendo">
                      <WatchersControl
                        tareaId={tarea.id}
                        watchers={tarea.watchers || []}
                        proyecto={proyecto}
                        currentEmpleadoId={user?.empleado_id}
                        onChanged={refetch}
                        variant="sidebar"
                        color={color}
                      />
                    </MetaRow>
                  </div>
                </div>

                {/* Dependencias */}
                <div className="mb-6">
                  <DependenciasSection
                    tarea={tarea}
                    proyecto={proyecto}
                    onChanged={refetch}
                  />
                </div>

                {/* Correos recordatorio a terceros (proveedor, otra area) */}
                <div className="mb-6">
                  <RecordatorioTercero tarea={tarea} proyecto={proyecto} />
                </div>

                {/* Adjuntos */}
                <div className="mb-6">
                  <Attachments
                    tareaId={tarea.id}
                    adjuntos={tarea.adjuntos || []}
                    onChanged={refetch}
                  />
                </div>

                {/* Relacionado con */}
                <RelatedEntities
                  tareaId={tarea.id}
                  tareaTitulo={tarea.titulo}
                  relaciones={tarea.relaciones || []}
                  onChanged={refetch}
                />
              </div>
            </div>
          )}

          {/* Footer cheatsheet de atajos. Discreto, solo en desktop. */}
          {tarea && (
            <div className="hidden md:flex items-center justify-end gap-3 px-5 py-1.5 border-t border-slate-200/70 bg-slate-50/40 text-[10px] text-slate-400 flex-shrink-0">
              <ShortcutHint k="Esc" label="cerrar" />
              <span className="text-slate-200">·</span>
              <ShortcutHint k="M" label="maximizar" />
              <span className="text-slate-200">·</span>
              <ShortcutHint k="C" label="comentar" />
              {onNavigate && (
                <>
                  <span className="text-slate-200">·</span>
                  <ShortcutHint k="←" label="anterior" />
                  <ShortcutHint k="→" label="siguiente" />
                </>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>

    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

// Pill compacto de atajo: `[K]` + label. Reusado en el footer del modal.
const ShortcutHint = ({ k, label }) => (
  <span className="inline-flex items-center gap-1">
    <kbd className="px-1 min-w-[16px] text-center font-mono text-[9px] font-medium bg-white text-slate-500 border border-slate-200 rounded leading-tight">
      {k}
    </kbd>
    <span>{label}</span>
  </span>
);

export default TaskSlideOver;
